import { useCallback, useEffect, useState } from "react";
import { ClipboardCheck, Loader2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";

const FIELDS = [
  ["findings", "Independent findings from the observed session", true],
  ["comparison", "Comparison with the trainer's reports — do they accurately reflect progress?", true],
  ["discrepancies", "Discrepancies identified", false],
  ["concerns", "Concerns", false],
  ["achievements", "Achievements", false],
  ["recommended_changes", "Recommended changes to the training plan", false],
  ["follow_up_actions", "Agreed follow-up actions", false],
];
const EMPTY = Object.fromEntries(FIELDS.map(([k]) => [k, ""]));

export default function VerificationPanel({ child, canManage }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [week, setWeek] = useState(null);
  const [form, setForm] = useState({ ...EMPTY, observation_date: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/verification-reviews", { params: { child_id: child.id } });
      setItems(data.items || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [child.id]);

  useEffect(() => { load(); }, [load]);

  const valid = form.observation_date && form.findings.trim() && form.comparison.trim();

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/verification-reviews", { child_id: child.id, review_week: week, ...form });
      toast.success(`Week ${week} verification review saved`);
      setWeek(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Co-ordinator progress verification reviews</CardTitle>
        <p className="text-xs text-slate-500">
          Before each parent review (weeks 4 and 8) the Co-ordinator reviews the trainer's weekly reports, observes one
          full live session, and records independent findings. The parent review cannot be recorded until this is done.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="flex items-center gap-2 text-slate-600" data-testid="verification-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          [4, 8].map((w) => {
            const done = items.find((r) => r.review_week === w);
            return (
              <div key={w} className="rounded-md border border-border p-3 space-y-2" data-testid={`verification-week-${w}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <ClipboardCheck className="h-4 w-4 text-primary" />
                  <span className="text-sm font-semibold text-slate-800">Week {w} review</span>
                  {done ? (
                    <Badge className="bg-emerald-50 text-emerald-700 border-transparent">Completed</Badge>
                  ) : (
                    <Badge className="bg-amber-50 text-amber-700 border-transparent">Outstanding</Badge>
                  )}
                  {done ? (
                    <span className="ml-auto text-xs text-slate-500">
                      {done.review_number} · observed {done.observation_date} · by {done.coordinator_name}
                    </span>
                  ) : canManage ? (
                    <Button size="sm" data-testid={`verification-start-week-${w}`}
                      className="ml-auto h-9 bg-primary hover:bg-brand-hover text-white"
                      onClick={() => { setWeek(w); setForm({ ...EMPTY, observation_date: "" }); }}>
                      Complete review
                    </Button>
                  ) : null}
                </div>
                {done && (
                  <div className="text-xs text-slate-600 space-y-1" data-testid={`verification-result-${w}`}>
                    <p><strong>Findings:</strong> {done.findings}</p>
                    <p><strong>Comparison with trainer reports:</strong> {done.comparison}</p>
                    {done.discrepancies && <p><strong>Discrepancies:</strong> {done.discrepancies}</p>}
                    {done.concerns && <p><strong>Concerns:</strong> {done.concerns}</p>}
                    {done.achievements && <p><strong>Achievements:</strong> {done.achievements}</p>}
                    {done.recommended_changes && <p><strong>Recommended changes:</strong> {done.recommended_changes}</p>}
                    {done.follow_up_actions && <p><strong>Follow-up actions:</strong> {done.follow_up_actions}</p>}
                  </div>
                )}
              </div>
            );
          })
        )}
      </CardContent>

      <Dialog open={!!week} onOpenChange={(v) => !v && setWeek(null)}>
        <DialogContent className="bg-white sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="verification-dialog">
          <DialogHeader>
            <DialogTitle>Week {week} progress verification review — {child.first_name} {child.surname}</DialogTitle>
            <DialogDescription>
              Confirm you have reviewed the trainer's weekly reports and personally observed one complete live training
              session from beginning to end, then record your independent findings below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="vr-date">Observation date (the live session you observed)</Label>
              <Input id="vr-date" data-testid="verification-date-input" type="date" className="h-11"
                value={form.observation_date} onChange={(e) => setForm((f) => ({ ...f, observation_date: e.target.value }))} />
            </div>
            {FIELDS.map(([key, label, required]) => (
              <div key={key} className="space-y-1.5">
                <Label htmlFor={`vr-${key}`}>{label}{required && <span className="text-red-600"> *</span>}</Label>
                <Textarea id={`vr-${key}`} data-testid={`verification-${key}`} rows={3} value={form[key]}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWeek(null)}>Cancel</Button>
            <Button data-testid="verification-save-button" disabled={saving || !valid} onClick={save}
              className="bg-primary hover:bg-brand-hover text-white font-semibold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save review"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
